<template>
  <!-- Add Timesheet -->
  <div class="modal fade dq-timesheet-modal" id="add_timesheet" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-md dq-timesheet-dialog">
      <div class="modal-content dq-modal-content" :key="addKey">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-blue">
              <i class="ti ti-clipboard-text"></i>
            </div>
            <div>
              <h4 class="modal-title dq-title mb-0">Add Today’s Work</h4>
              <div class="dq-subtitle">
                Log your hours, progress, and notes for the day.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitCreate">
          <div class="modal-body dq-body pb-0">
            <!-- Employee + Project -->
            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-blue">
                    <i class="ti ti-user"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Who & What</div>
                    <div class="dq-help">Select employee and project details.</div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="row g-3">
                  <div class="col-12" v-if="!isEmployee">
                    <label class="form-label dq-label">
                      Employee <span class="text-danger">*</span>
                    </label>
                    <select v-model="form.employee_id" class="form-select dq-input">
                      <option value="">Select</option>
                      <option v-for="e in employeesForSelect" :key="e.id" :value="e.id">
                        {{ e.label }}
                      </option>
                    </select>
                  </div>

                  <div class="col-12" v-else>
                    <label class="form-label dq-label">Employee</label>
                    <div class="form-control dq-input dq-readonly">
                      {{ auth.user?.first_name }} {{ auth.user?.last_name }}
                    </div>
                  </div>

                  <div class="col-12">
                    <label class="form-label dq-label">
                      Project <span class="text-danger">*</span>
                    </label>
                    <input
                      v-model.trim="form.project"
                      type="text"
                      class="form-control dq-input"
                      placeholder="e.g. Office Management"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Dates -->
            <div class="dq-section mt-3">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-indigo">
                    <i class="ti ti-calendar"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Dates</div>
                    <div class="dq-help">Set the work date and deadline (if any).</div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="row g-3">
                  <div class="col-12">
                    <label class="form-label dq-label">
                      Deadline <span class="text-danger">*</span>
                    </label>
                    <div class="input-icon-end position-relative">
                      <date-picker
                        v-model="form.deadline"
                        class="form-control dq-input datetimepicker"
                        placeholder="dd/mm/yyyy"
                        :editable="true"
                        :clearable="false"
                        :input-format="dateFormat"
                      />
                      <span class="input-icon-addon">
                        <i class="ti ti-calendar text-gray-7"></i>
                      </span>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <label class="form-label dq-label">Work Date</label>
                    <div class="input-icon-end position-relative">
                      <date-picker
                        v-model="form.work_date"
                        class="form-control dq-input datetimepicker"
                        placeholder="dd/mm/yyyy"
                        :editable="true"
                        :clearable="false"
                        :input-format="dateFormat"
                      />
                      <span class="input-icon-addon">
                        <i class="ti ti-calendar text-gray-7"></i>
                      </span>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <label class="form-label dq-label">Status</label>
                    <select v-model="form.status" class="form-select dq-input">
                      <option>Draft</option>
                      <option>Submitted</option>
                      <option v-if="canApproveTimesheets">Approved</option>
                      <option v-if="canApproveTimesheets">Rejected</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Hours -->
            <div class="dq-section mt-3">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-green">
                    <i class="ti ti-clock"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Hours</div>
                    <div class="dq-help">Track planned vs actual work time.</div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label dq-label">
                      Total Hours <span class="text-danger">*</span>
                    </label>
                    <input
                      v-model.number="form.total_hours"
                      type="number"
                      step="0.25"
                      min="0"
                      class="form-control dq-input"
                      placeholder="e.g. 8"
                    />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label dq-label">
                      Worked Hours <span class="text-danger">*</span>
                    </label>
                    <input
                      v-model.number="form.worked_hours"
                      type="number"
                      step="0.25"
                      min="0"
                      class="form-control dq-input"
                      placeholder="e.g. 6.5"
                    />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label dq-label">Remaining Hours</label>
                    <input
                      v-model.number="form.remaining_hours"
                      type="number"
                      step="0.25"
                      min="0"
                      class="form-control dq-input"
                      placeholder="e.g. 1.5"
                    />
                  </div>

                  <div class="col-md-6">
                    <div class="dq-mini-tip">
                      <i class="ti ti-info-circle me-1"></i>
                      Tip: Use 0.25 steps (15 min increments).
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Notes -->
            <div class="dq-section mt-3">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-purple">
                    <i class="ti ti-notes"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Notes</div>
                    <div class="dq-help">Summarize what you worked on (optional).</div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <label class="form-label dq-label">Notes</label>
                <textarea
                  v-model.trim="form.notes"
                  class="form-control dq-input"
                  rows="3"
                  placeholder="What did you work on?"
                ></textarea>
              </div>
            </div>
          </div>

          <div class="modal-footer dq-footer">
            <button type="button" class="btn btn-light dq-btn dq-btn-muted me-2" data-bs-dismiss="modal">
              Cancel
            </button>
            <button type="submit" class="btn btn-primary dq-btn dq-btn-primary">
              <span v-if="store.loading" class="spinner-border spinner-border-sm me-2"></span>
              Add Changes
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Edit Timesheet -->
  <div class="modal fade dq-timesheet-modal" id="edit_timesheet" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-md dq-timesheet-dialog">
      <div class="modal-content dq-modal-content" :key="editKey">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-amber">
              <i class="ti ti-edit"></i>
            </div>
            <div>
              <h4 class="modal-title dq-title mb-0">Edit Today’s Work</h4>
              <div class="dq-subtitle">
                Update your timesheet entry without losing context.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitEdit">
          <div class="modal-body dq-body pb-0">
            <!-- Same sections as add -->
            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-blue">
                    <i class="ti ti-user"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Who & What</div>
                    <div class="dq-help">Confirm employee and project details.</div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="row g-3">
                  <div class="col-12" v-if="!isEmployee">
                    <label class="form-label dq-label">Employee</label>
                    <select v-model="form.employee_id" class="form-select dq-input">
                      <option value="">Select</option>
                      <option v-for="e in employeesForSelect" :key="e.id" :value="e.id">
                        {{ e.label }}
                      </option>
                    </select>
                  </div>

                  <div class="col-12" v-else>
                    <label class="form-label dq-label">Employee</label>
                    <div class="form-control dq-input dq-readonly">
                      {{ auth.user?.first_name }} {{ auth.user?.last_name }}
                    </div>
                  </div>

                  <div class="col-12">
                    <label class="form-label dq-label">Project</label>
                    <input v-model.trim="form.project" type="text" class="form-control dq-input" />
                  </div>
                </div>
              </div>
            </div>

            <div class="dq-section mt-3">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-indigo">
                    <i class="ti ti-calendar"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Dates</div>
                    <div class="dq-help">Adjust deadline, work date and status.</div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="row g-3">
                  <div class="col-12">
                    <label class="form-label dq-label">Deadline</label>
                    <div class="input-icon-end position-relative">
                      <date-picker
                        v-model="form.deadline"
                        class="form-control dq-input datetimepicker"
                        placeholder="dd/mm/yyyy"
                        :editable="true"
                        :clearable="false"
                        :input-format="dateFormat"
                      />
                      <span class="input-icon-addon">
                        <i class="ti ti-calendar text-gray-7"></i>
                      </span>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <label class="form-label dq-label">Date</label>
                    <div class="input-icon-end position-relative">
                      <date-picker
                        v-model="form.work_date"
                        class="form-control dq-input datetimepicker"
                        placeholder="dd/mm/yyyy"
                        :editable="true"
                        :clearable="false"
                        :input-format="dateFormat"
                      />
                      <span class="input-icon-addon">
                        <i class="ti ti-calendar text-gray-7"></i>
                      </span>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <label class="form-label dq-label">Status</label>
                    <select v-model="form.status" class="form-select dq-input">
                      <option>Draft</option>
                      <option>Submitted</option>
                      <option v-if="canApproveTimesheets">Approved</option>
                      <option v-if="canApproveTimesheets">Rejected</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div class="dq-section mt-3">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-green">
                    <i class="ti ti-clock"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Hours</div>
                    <div class="dq-help">Update planned vs actual time.</div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label dq-label">Total Hours</label>
                    <input
                      v-model.number="form.total_hours"
                      type="number"
                      step="0.25"
                      min="0"
                      class="form-control dq-input"
                    />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label dq-label">Worked Hours</label>
                    <input
                      v-model.number="form.worked_hours"
                      type="number"
                      step="0.25"
                      min="0"
                      class="form-control dq-input"
                    />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label dq-label">Remaining Hours</label>
                    <input
                      v-model.number="form.remaining_hours"
                      type="number"
                      step="0.25"
                      min="0"
                      class="form-control dq-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div class="dq-section mt-3">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-purple">
                    <i class="ti ti-notes"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Notes</div>
                    <div class="dq-help">Keep a short, useful summary.</div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <label class="form-label dq-label">Notes</label>
                <textarea v-model.trim="form.notes" class="form-control dq-input" rows="3"></textarea>
              </div>
            </div>
          </div>

          <div class="modal-footer dq-footer">
            <button type="button" class="btn btn-light dq-btn dq-btn-muted me-2" data-bs-dismiss="modal">
              Cancel
            </button>
            <button type="submit" class="btn btn-primary dq-btn dq-btn-primary" :disabled="!isEdit">
              <span v-if="store.loading" class="spinner-border spinner-border-sm me-2"></span>
              Save Changes
              <i class="ti ti-check ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Delete Modal -->
  <div class="modal fade dq-timesheet-modal" id="delete_modal">
    <div class="modal-dialog modal-dialog-centered modal-sm dq-delete-dialog">
      <div class="modal-content dq-modal-content">
        <div class="modal-body text-center dq-delete-body">
          <span class="avatar avatar-xl bg-transparent-danger text-danger mb-3 dq-danger-avatar">
            <i class="ti ti-trash-x fs-36"></i>
          </span>
          <h4 class="mb-1 dq-delete-title">Confirm Delete</h4>
          <p class="mb-3 dq-delete-sub">Are you sure you want to delete this timesheet record?</p>

          <div class="d-flex justify-content-center">
            <a href="javascript:void(0);" class="btn btn-light me-3 dq-btn dq-btn-muted" data-bs-dismiss="modal">
              Cancel
            </a>
            <button class="btn btn-danger dq-btn dq-btn-danger" :disabled="deleting" @click="confirmDelete">
              <span v-if="deleting" class="spinner-border spinner-border-sm me-2"></span>
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { useTimesheetStore } from "@/stores/timesheetStore";
import { useEmployeeStore } from "@/stores/employeeStore";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "@/utils/toast";

const props = defineProps({ editItem: { type: Object, default: null } });
const emit = defineEmits(["saved"]);

const store = useTimesheetStore();
const empStore = useEmployeeStore();
const auth = useAuthStore();

const deleting = ref(false);

const dateFormat = "dd/MM/yyyy";
const addKey = ref(0);
const editKey = ref(0);

const isEmployee = computed(() => {
  const roles = Array.isArray(auth.user?.roles) ? auth.user.roles : [];
  const roleNames = roles.map((role) => (typeof role === "string" ? role : role?.name));
  const isAdminSide = roleNames.includes("Admin") || roleNames.includes("HR") || roleNames.includes("Hr");

  return !!auth.user?.employee && !isAdminSide;
});
const canApproveTimesheets = computed(() => {
  const roles = Array.isArray(auth.user?.roles) ? auth.user.roles : [];
  return roles.some((role) => {
    const name = typeof role === "string" ? role : role?.name;
    return name === "Admin" || name === "HR" || name === "Hr";
  });
});

const form = ref(blankForm());
function blankForm() {
  return {
    employee_id: "",
    project: "",
    deadline: null,
    total_hours: "",
    remaining_hours: "",
    work_date: null,
    worked_hours: "",
    notes: "",
    status: "Submitted",
  };
}
function resetForm() {
  const b = blankForm();
  for (const k in b) form.value[k] = b[k];
}

const employeesForSelect = computed(() =>
  (empStore.employees || []).map((e) => ({
    id: e.id,
    label: `${e?.user?.first_name ?? e.first_name ?? ""} ${
      e?.user?.last_name ?? e.last_name ?? ""
    } (${e.employee_id})`.trim(),
  }))
);

watch(
  () => props.editItem,
  (val) => {
    if (val) {
      form.value.employee_id = val.employee_id ?? "";
      form.value.project = val.project ?? "";
      form.value.deadline = val.deadline ? new Date(val.deadline) : null;
      form.value.total_hours = val.total_hours ?? "";
      form.value.remaining_hours = val.remaining_hours ?? "";
      form.value.work_date = val.work_date ? new Date(val.work_date) : null;
      form.value.worked_hours = val.worked_hours ?? "";
      form.value.notes = val.notes ?? "";
      form.value.status = val.status ?? "Submitted";
      editKey.value++;
    } else {
      resetForm();
    }
  },
  { immediate: true }
);

const isEdit = computed(() => !!props.editItem?.id);

function toYMD(d) {
  if (!d) return null;
  if (typeof d === "string") return d;
  try {
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch {
    return null;
  }
}

async function submitCreate() {
  if (isEmployee.value && selfEmployeeId.value && !form.value.employee_id) {
    form.value.employee_id = selfEmployeeId.value;
  }
  if (!form.value.employee_id || !form.value.project || !form.value.work_date) {
    toast("Employee, Project and Date are required", "error");
    return;
  }
  const payload = {
    ...form.value,
    deadline: toYMD(form.value.deadline),
    work_date: toYMD(form.value.work_date),
  };
  await store.create(payload);
  closeAdd();
  resetForm();
  addKey.value++;
  emit("saved");
}

async function submitEdit() {
  if (!props.editItem?.id) return;
  const payload = {
    ...form.value,
    deadline: toYMD(form.value.deadline),
    work_date: toYMD(form.value.work_date),
  };
  await store.update(props.editItem.id, payload);
  closeEdit();
  resetForm();
  editKey.value++;
  emit("saved");
}

function closeAdd() {
  document.querySelector('#add_timesheet [data-bs-dismiss="modal"]')?.click();
}
function closeEdit() {
  document.querySelector('#edit_timesheet [data-bs-dismiss="modal"]')?.click();
}

async function confirmDelete() {
  deleting.value = true;
  try {
    if (!props.editItem?.id) return;
    await store.remove(props.editItem.id);
    emit("saved");
  } catch (e) {
    console.error(e);
  } finally {
    deleting.value = false;
  }
}

const selfEmployeeId = computed(() => auth.user?.employee?.id || null);

watch(
  canApproveTimesheets,
  (allowed) => {
    if (!allowed && ["Approved", "Rejected"].includes(form.value.status)) {
      form.value.status = "Submitted";
    }
  },
  { immediate: true }
);

watch(selfEmployeeId, (id) => {
  if (isEmployee.value && id && !props.editItem) {
    form.value.employee_id = id;
  }
});

let cleanup = null;
onMounted(() => {
  if (!isEmployee.value && !empStore.employees?.length) {
    empStore.fetchEmployees().catch(() => {});
  }

  const addEl = document.getElementById("add_timesheet");
  const editEl = document.getElementById("edit_timesheet");

  const onAddShow = () => {
    resetForm();
    if (isEmployee.value && selfEmployeeId.value) {
      form.value.employee_id = selfEmployeeId.value;
    }
    addKey.value++;
  };
  const onAddHidden = () => resetForm();
  const onEditHidden = () => {
    resetForm();
    editKey.value++;
  };

  addEl?.addEventListener("show.bs.modal", onAddShow);
  addEl?.addEventListener("hidden.bs.modal", onAddHidden);
  editEl?.addEventListener("hidden.bs.modal", onEditHidden);

  cleanup = () => {
    addEl?.removeEventListener("show.bs.modal", onAddShow);
    addEl?.removeEventListener("hidden.bs.modal", onAddHidden);
    editEl?.removeEventListener("hidden.bs.modal", onEditHidden);
  };
});
onBeforeUnmount(() => cleanup && cleanup());
</script>

<style scoped>
/* Wider modal */
.dq-timesheet-dialog {
  max-width: 920px;
}
@media (max-width: 768px) {
  .dq-timesheet-dialog {
    max-width: 96%;
  }
}
.dq-delete-dialog {
  max-width: 520px;
}

/* Shell */
.dq-modal-content {
  border-radius: 22px;
  border: none;
  overflow: hidden;
  box-shadow:
    0 10px 25px rgba(15, 23, 42, 0.08),
    0 40px 80px rgba(15, 23, 42, 0.12);
}

/* Header */
.dq-modal-header {
  padding: 18px 22px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, rgba(59, 130, 246, 0.10), #fff);
}
.dq-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-title {
  font-weight: 900;
  letter-spacing: -0.35px;
}
.dq-subtitle {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-close {
  opacity: 0.85;
}
.dq-close:hover {
  opacity: 1;
}

/* Hero icons */
.dq-hero {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.dq-hero-blue {
  background: rgba(59, 130, 246, 0.16);
  color: rgb(37, 99, 235);
}
.dq-hero-amber {
  background: rgba(245, 158, 11, 0.18);
  color: rgb(180, 83, 9);
}

/* Body */
.dq-body {
  padding: 18px 22px;
}

/* Sections */
.dq-section {
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.02);
  overflow: hidden;
}
.dq-section-head {
  padding: 14px 14px 0 14px;
}
.dq-section-body {
  padding: 14px;
}
.dq-section-title {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.dq-section-badge {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex: 0 0 auto;
  background: rgba(15, 23, 42, 0.05);
}
.dq-badge-blue {
  background: rgba(59, 130, 246, 0.12);
  color: rgb(37, 99, 235);
}
.dq-badge-indigo {
  background: rgba(99, 102, 241, 0.12);
  color: rgb(79, 70, 229);
}
.dq-badge-green {
  background: rgba(34, 197, 94, 0.12);
  color: rgb(22, 163, 74);
}
.dq-badge-purple {
  background: rgba(168, 85, 247, 0.12);
  color: rgb(126, 34, 206);
}

.dq-h6 {
  font-weight: 900;
  letter-spacing: -0.2px;
}
.dq-help {
  font-size: 0.8rem;
  color: rgba(15, 23, 42, 0.55);
  line-height: 1.35;
}
.dq-label {
  font-size: 0.82rem;
  font-weight: 800;
}

/* Inputs */
.dq-input {
  border-radius: 14px;
  padding: 10px 12px;
  transition: all 0.15s ease;
}
.dq-input:focus {
  border-color: rgba(59, 130, 246, 0.55);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.14);
}
.dq-readonly {
  background: rgba(15, 23, 42, 0.04);
  border-color: rgba(15, 23, 42, 0.06);
}

/* Tiny tip */
.dq-mini-tip {
  padding: 12px;
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.03);
  color: rgba(15, 23, 42, 0.65);
  font-size: 0.82rem;
  display: flex;
  align-items: center;
  height: 100%;
}

/* Footer buttons */
.dq-footer {
  padding: 14px 22px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
}
.dq-btn {
  border-radius: 12px;
  padding: 10px 16px;
  font-weight: 800;
}
.dq-btn-muted {
  font-weight: 700;
}
.dq-btn-primary {
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.08);
}

/* Delete modal polish */
.dq-delete-body {
  padding: 22px;
}
.dq-danger-avatar {
  border-radius: 18px;
}
.dq-delete-title {
  font-weight: 900;
}
.dq-delete-sub {
  color: rgba(15, 23, 42, 0.6);
}
.dq-btn-danger {
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.08);
}
</style>
